import { FC, useEffect, useMemo, useState } from 'react';

import { bugoUrl } from '@shared/api/bugo/bugo.hook';
import { BugoData } from '@shared/api/bugo/bugo.interface';
import {
  getGiftOrderThanksPrints,
  listGiftBrandSellerDeliveries,
  updateGiftDeliveryThanksPrint,
  updateGiftOrderThanksPrint,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryOrderInfo,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import {
  GiftItemBrand,
  isThanksPrintBoxSet,
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';
import { api_ } from '@shared/plugIn/axios';
import { Gender, LowerRole } from '@shared/types';

import { statusLabel } from '../../giftSeller/utils/delivery.constants';
import {
  getBrandItemName,
  getOrdererInfo,
} from '../../giftSeller/utils/delivery.utils';
import { GiftThanksPrintPreview } from '../../giftStore/containers/GiftThanksPrintSection/GiftThanksPrintPreview';
import {
  clampThanksMessage,
  composeThanksSender,
  fillThanksMessage,
  normalizeThanksPreset,
  parseThanksSender,
  templateMessagePresets,
  THANKS_FALLBACK_NAME,
  THANKS_MESSAGE_MAX_CHARS,
  THANKS_MESSAGE_MAX_LINES,
  THANKS_MESSAGE_PRESETS,
  THANKS_SENDER_SUFFIXES,
  thanksMessageCharCount,
  thanksSenderNameLimit,
  ThanksSenderSuffix,
} from '../../giftStore/utils/thanksPrintMessage';

type ThanksPrintValue = NonNullable<GiftDeliveryForAdmin['thanksPrint']>;

interface Props {
  delivery: GiftDeliveryForAdmin | null;
  templates: ThanksPrintTemplate<Image>[];
  onClose: () => void;
  /**
   * 수정/추가 가능 여부 (admin 전용). 배송관리자 경로는 서버가 이 필드를 버리므로 조회만.
   */
  editable?: boolean;
  /** 카드 추가·템플릿 변경 시 해당 배송 건의 브랜드 템플릿만 고르게 하기 위한 브랜드 목록 */
  brands?: GiftItemBrand[] | null;
  /** 저장 완료 — 방금 연 건에 반영된 값을 돌려준다 (목록 갱신·모달 표시 동기화용) */
  onSaved?: (deliveryId: string, thanksPrint: ThanksPrintValue) => void;
}

/** 클립보드 복사 (보안 컨텍스트가 아닌 환경 대비 fallback 포함) */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // execCommand fallback 으로 진행
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

// 감사글 / 보내는분을 PSD 등에 바로 붙여넣을 수 있도록 복사 가능한 필드
const CopyField: FC<{ label: string; value: string; multiline?: boolean }> =
  function CopyField({ label, value, multiline }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      const ok = await copyToClipboard(value || '');
      if (!ok) {
        alert('복사에 실패했습니다. 직접 선택하여 복사해 주세요.');
        return;
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    };

    return (
      <div className="overflow-hidden rounded-lg border bg-gray-50">
        <div className="flex items-center justify-between border-b bg-white px-3 py-2">
          <span className="text-xs font-semibold text-gray-600">{label}</span>
          <button
            onClick={handleCopy}
            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              copied
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
            }`}
          >
            {copied ? '복사됨 ✓' : '복사'}
          </button>
        </div>
        <p
          className={`px-3 py-2 text-sm text-gray-800 ${
            multiline ? 'min-h-[3rem] whitespace-pre-wrap' : ''
          }`}
        >
          {value || '-'}
        </p>
      </div>
    );
  };

const getOrderId = (d: GiftDeliveryForAdmin): string =>
  typeof d.giftOrder === 'object' ? d.giftOrder?._id ?? '' : d.giftOrder ?? '';

const getBugoId = (d: GiftDeliveryForAdmin): string =>
  typeof d.giftOrder === 'object'
    ? (d.giftOrder as GiftDeliveryOrderInfo).bugo?._id ?? ''
    : '';

const getBrandId = (d: GiftDeliveryForAdmin): string =>
  typeof d.giftItemBrand === 'object' ? d.giftItemBrand?._id ?? '' : d.giftItemBrand ?? '';

const getGiftItemId = (d: GiftDeliveryForAdmin): string =>
  typeof d.giftItem === 'object' ? d.giftItem?._id ?? '' : d.giftItem ?? '';

const getItemInfo = (d: GiftDeliveryForAdmin) => {
  const info =
    typeof d.giftItem === 'object'
      ? (d.giftItem as GiftItemForStore).giftItemInfo
      : undefined;
  return info && typeof info === 'object' ? info : undefined;
};

/** 이미 업체로 발주가 넘어간 상태 — 저장해도 업체가 받은 발주서·연동 데이터는 안 바뀐다 */
const ORDERED_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
  GiftDeliveryStatus.Delivered,
];

/**
 * 이 배송 건의 상품이 감사카드 대상인지 — 서버 주문 생성과 같은 판정.
 * 상품 오버라이드(useThanksPrint) ?? 브랜드(thanksPrint.enabled) ?? false
 */
export const isThanksCardItem = (
  delivery: GiftDeliveryForAdmin,
  brands: GiftItemBrand[] | null | undefined,
): boolean => {
  const brand = (brands ?? []).find((b) => b._id === getBrandId(delivery));
  if (!brand?.thanksPrint) return false;
  return Boolean(getItemInfo(delivery)?.useThanksPrint ?? brand.thanksPrint.enabled);
};

/**
 * 이 배송 건에서 고를 수 있는 디자인(스티커).
 * 브랜드 템플릿 중 사용중(enabled)인 것 → 상품이 허용 템플릿을 지정했으면 그 교집합.
 * 현재 카드에 걸린 템플릿은 비활성·비허용이어도 남긴다(수정하다 디자인이 사라지면 안 됨).
 * 브랜드를 못 찾으면(브랜드 미연결 건) 전체 템플릿으로 폴백.
 */
export const selectableThanksTemplates = (
  delivery: GiftDeliveryForAdmin,
  brands: GiftItemBrand[] | null | undefined,
  allTemplates: ThanksPrintTemplate<Image>[],
): ThanksPrintTemplate<Image>[] => {
  const brandId = getBrandId(delivery);
  const brand = brandId ? (brands ?? []).find((b) => b._id === brandId) : undefined;
  const pool = brand ? brand.thanksPrint?.templates ?? [] : allTemplates;
  const currentId = delivery.thanksPrint?.templateId
    ? String(delivery.thanksPrint.templateId)
    : undefined;

  const allowedIds = getItemInfo(delivery)?.thanksPrintTemplates ?? [];
  const allowed = allowedIds.length ? new Set(allowedIds.map(String)) : undefined;

  return pool
    .filter((t): t is ThanksPrintTemplate<Image> & { _id: string } => !!t._id)
    .filter(
      (t) =>
        t._id === currentId ||
        (t.enabled !== false && (!allowed || allowed.has(t._id))),
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/** 카드 없이 들어온 건에 [+ 감사카드]를 띄울지 — 감사카드 대상 상품 + 고를 디자인이 있을 때 */
export const canAddThanksCard = (
  delivery: GiftDeliveryForAdmin,
  brands: GiftItemBrand[] | null | undefined,
): boolean =>
  !delivery.thanksPrint &&
  delivery.deliveryStatus !== GiftDeliveryStatus.Canceled &&
  isThanksCardItem(delivery, brands) &&
  selectableThanksTemplates(delivery, brands, []).length > 0;

/**
 * 폼 입력 → 저장값. 스토어 주문(buildThanksPrintDto)·서버 검증과 같은 규칙:
 * - 감사글 영역(messageBox)이 없는 디자인 = 문구가 이미 인쇄된 스티커 → 이름만. message는 첫 지정 문구 또는 ''
 * - 지정 문구 세트가 있으면 세트 원본 그대로 (세트 밖 문구는 서버가 주문 때 거부하는 값)
 * - 그 외 자유 입력
 */
export const resolveThanksCardValue = (
  template: ThanksPrintTemplate<Image> & { _id: string },
  input: { message: string; senderName: string; suffix: ThanksSenderSuffix },
): ThanksPrintValue => {
  const nameLimit = thanksSenderNameLimit(template);
  const name = input.senderName.trim();
  const senderName = composeThanksSender(
    nameLimit !== undefined ? name.slice(0, nameLimit) : name,
    input.suffix,
  );
  const presets = templateMessagePresets(template);
  if (!isThanksPrintBoxSet(template.messageBox)) {
    return { templateId: template._id, message: presets[0] ?? '', senderName };
  }
  const message = input.message.replace(/\r\n/g, '\n').trim();
  return {
    templateId: template._id,
    message: presets.length
      ? presets.find((p) => normalizeThanksPreset(p) === normalizeThanksPreset(message)) ??
        presets[0] ??
        ''
      : message,
    senderName,
  };
};

const isSameCard = (a?: ThanksPrintValue, b?: ThanksPrintValue): boolean =>
  !!a &&
  !!b &&
  String(a.templateId) === String(b.templateId) &&
  a.message === b.message &&
  a.senderName === b.senderName;

/** 감사글 한 줄 요약 (버튼 라벨용) */
const oneLine = (s: string) => normalizeThanksPreset(s).replace(/\s*\n\s*/g, ' ');

const inputClassName =
  'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

interface InnerProps extends Omit<Props, 'delivery'> {
  delivery: GiftDeliveryForAdmin;
}

const ThanksCardModalInner: FC<InnerProps> = function ThanksCardModalInner({
  delivery,
  templates,
  onClose,
  editable,
  brands,
  onSaved,
}) {
  const tp = delivery.thanksPrint;
  const isNewCard = !tp;

  // 카드가 없는 건은 곧바로 작성 화면으로 연다
  const [isEditing, setIsEditing] = useState(isNewCard);
  const [isSaving, setIsSaving] = useState(false);

  const candidates = useMemo(
    () => selectableThanksTemplates(delivery, brands, templates),
    [delivery, brands, templates],
  );

  // 상주·고인 정보 — 새 카드 기본값(스토어와 같은 "상주 이름 배상" + "저희 ○○ 상중" 감사글)용
  const [mourner, setMourner] = useState<{
    name?: string;
    relationType?: string;
    deceasedSex?: Gender;
  } | null>(null);
  const bugoId = getBugoId(delivery);
  useEffect(() => {
    if (!isNewCard || !bugoId) return undefined;
    let alive = true;
    api_
      .get<BugoData>(bugoUrl(bugoId))
      .then(({ data }) => {
        if (!alive) return;
        setMourner({
          name: data?.member?.fullName?.trim() || undefined,
          relationType: data?.member?.relationType,
          deceasedSex: data?.fevent?.deceasedInfo?.sex,
        });
      })
      // 부고를 못 읽어도(삭제·익명화 등) 주문자 이름·"상중" 기본값으로 작성은 가능
      .catch(() => alive && setMourner({}));
    return () => {
      alive = false;
    };
  }, [isNewCard, bugoId]);

  const defaultFreeMessage = (relationType?: string, deceasedSex?: Gender) =>
    clampThanksMessage(fillThanksMessage(THANKS_MESSAGE_PRESETS[0] ?? '', relationType, deceasedSex));

  // 폼 값은 열릴 때 1회만 채운다(키=배송 id 로 리마운트). 목록 재조회가 입력을 덮어쓰지 않게 effect 시딩 금지
  const [form, setForm] = useState(() => {
    const parsed = tp
      ? parseThanksSender(tp.senderName, undefined)
      : { name: '', suffix: '배상' as ThanksSenderSuffix };
    const ordererName = getOrdererInfo(delivery).name;
    const initialTemplate = tp?.templateId
      ? candidates.find((t) => t._id === String(tp.templateId))
      : candidates[0];
    return {
      // 지금 디자인을 이 상품에서 고를 수 없으면(예: 예전 버그로 다른 브랜드 디자인이 붙은 건) 첫 후보로 —
      // 목록에 없는 id를 들고 있으면 드롭다운은 첫 항목을 보여 주는데 저장은 남의 디자인으로 된다
      templateId: initialTemplate?._id ?? candidates[0]?._id ?? '',
      message: tp?.message ?? defaultFreeMessage(),
      senderName: tp ? parsed.name : ordererName === '-' ? '' : ordererName,
      suffix: parsed.suffix,
    };
  });
  // 운영자가 직접 손댄 값은 늦게 도착한 상주 정보로 덮어쓰지 않는다
  const [touched, setTouched] = useState({ message: false, sender: false });

  const formTemplate = useMemo(
    () =>
      candidates.find((t) => t._id === form.templateId) ??
      templates.find((t) => t._id === form.templateId),
    [candidates, templates, form.templateId],
  );
  const hasMessageArea = isThanksPrintBoxSet(formTemplate?.messageBox);
  const presets = useMemo(() => templateMessagePresets(formTemplate), [formTemplate]);
  const nameLimit = thanksSenderNameLimit(formTemplate);

  // 템플릿 목록이 늦게 도착해도 새 카드 작성 시 첫 디자인이 골라져 있게 (미선택일 때만)
  const firstCandidateId = candidates[0]?._id;
  useEffect(() => {
    if (!firstCandidateId) return;
    setForm((prev) => (prev.templateId ? prev : { ...prev, templateId: firstCandidateId }));
  }, [firstCandidateId]);

  // 상주 정보 도착 → 손대지 않은 칸만 스토어 기본값으로
  useEffect(() => {
    if (!mourner) return;
    setForm((prev) => ({
      ...prev,
      ...(!touched.sender && mourner.name && { senderName: mourner.name }),
      ...(!touched.message && {
        message: defaultFreeMessage(mourner.relationType, mourner.deceasedSex),
      }),
    }));
    // 도착 시 1회
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mourner]);

  // 디자인 전환 시 스토어와 같은 보정:
  // 지정 문구 세트가 있는 디자인이면 세트 밖 감사글을 첫 문구로, 이름 제한이 있으면 초과분 자르기.
  // 값에도 의존해 늦게 도착한 상주 기본값이 세트 밖 문구를 넣어도 다시 보정된다(맞으면 no-op이라 루프 없음)
  useEffect(() => {
    setForm((prev) => {
      let next = prev;
      if (
        hasMessageArea &&
        presets.length > 0 &&
        !presets.some((p) => normalizeThanksPreset(p) === normalizeThanksPreset(prev.message))
      ) {
        next = { ...next, message: presets[0] ?? '' };
      }
      if (nameLimit !== undefined && next.senderName.length > nameLimit) {
        next = { ...next, senderName: next.senderName.slice(0, nameLimit) };
      }
      return next;
    });
  }, [hasMessageArea, presets, nameLimit, form.message, form.senderName]);

  // 같은 주문의 다른 배송 건 — 같은 브랜드 감사카드 상품은 대개 같은 카드라 함께 고칠 수 있게 (디자인이 안 맞는 건은 제외)
  const orderId = getOrderId(delivery);
  const [siblings, setSiblings] = useState<GiftDeliveryForAdmin[]>([]);
  const [siblingIds, setSiblingIds] = useState<Set<string>>(new Set());
  const [siblingsFailed, setSiblingsFailed] = useState(false);
  useEffect(() => {
    if (!editable || !isEditing || !orderId) return undefined;
    let alive = true;
    listGiftBrandSellerDeliveries({ giftOrder: orderId }, { seq: 1 }, LowerRole.Admin)
      .then((list) => {
        if (!alive) return;
        const others = list.filter(
          (d) =>
            d._id !== delivery._id &&
            d.deliveryStatus !== GiftDeliveryStatus.Canceled &&
            // 같은 브랜드의 감사카드 대상 상품만 — 다른 브랜드 디자인은 그 상품에 인쇄될 수 없다
            getBrandId(d) === getBrandId(delivery) &&
            isThanksCardItem(d, brands) &&
            // 수정: 카드가 있는 건 / 추가: 카드가 없는 건
            (tp ? !!d.thanksPrint : !d.thanksPrint),
        );
        setSiblings(others);
        // 수정은 지금 카드와 내용이 같은 건만 기본 체크(따로 고쳐둔 건은 건드리지 않음)
        setSiblingIds(
          new Set(
            others
              .filter((d) => (tp ? isSameCard(d.thanksPrint, tp) : true))
              .map((d) => d._id),
          ),
        );
      })
      .catch(() => alive && setSiblingsFailed(true));
    return () => {
      alive = false;
    };
    // 편집 진입 시 1회
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, isEditing, orderId, delivery._id]);

  // 상품마다 허용 디자인(스티커)이 다를 수 있다 — 고른 디자인을 못 쓰는 건은 함께 적용 대상에서 뺀다
  const siblingAllows = useMemo(
    () =>
      new Map(
        siblings.map((d) => [
          d._id,
          selectableThanksTemplates(d, brands, templates).some((t) => t._id === form.templateId),
        ]),
      ),
    [siblings, brands, templates, form.templateId],
  );
  const siblingTargets = siblings.filter(
    (d) => siblingIds.has(d._id) && siblingAllows.get(d._id),
  );

  const viewTemplate = useMemo(
    () =>
      tp?.templateId
        ? templates.find((t) => t._id === String(tp.templateId))
        : undefined,
    [templates, tp?.templateId],
  );

  const composedSender = form.senderName.trim()
    ? composeThanksSender(form.senderName, form.suffix)
    : '';

  const nameTooLong = nameLimit !== undefined && form.senderName.trim().length > nameLimit;
  const invalidReason = !formTemplate?._id
    ? '카드 디자인 선택'
    : hasMessageArea && !presets.length && !form.message.trim()
    ? '감사글 입력'
    : !form.senderName.trim()
    ? '보내는 분 입력'
    : nameTooLong
    ? `보내는 분 ${nameLimit}자 이내`
    : '';

  const isOrdered = ORDERED_STATUSES.includes(delivery.deliveryStatus);

  const toggleSibling = (id: string) =>
    setSiblingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSave = async () => {
    if (invalidReason || isSaving || !formTemplate?._id) return;
    const value = resolveThanksCardValue(
      formTemplate as ThanksPrintTemplate<Image> & { _id: string },
      form,
    );
    const targets = [delivery._id, ...siblingTargets.map((d) => d._id)];
    setIsSaving(true);
    const failed: string[] = [];
    for (const id of targets) {
      try {
        await updateGiftDeliveryThanksPrint(id, value);
      } catch (err) {
        console.error(err);
        failed.push(id);
      }
    }
    if (failed.includes(delivery._id)) {
      setIsSaving(false);
      alert('감사카드 저장에 실패했습니다.');
      return;
    }

    // 주문 원본도 맞춘다 — 그 카드를 받는 배송 건을 전부 고쳤을 때만 (일부만 고쳤으면 원본 유지)
    // - 카드 묶음 주문: 이 배송 건 상품이 속한 묶음(thanksPrints[i])만. 묶음 밖(나중에 붙인 카드)은 원본 없음
    // - 구 주문(카드 1장): 같은 브랜드 카드 배송 건 전체를 고쳤을 때 thanksPrint
    let orderSyncFailed = false;
    if (orderId && !siblingsFailed && failed.length === 0) {
      try {
        const { groups, representativeTemplateId } = await getGiftOrderThanksPrints(orderId);
        const itemId = getGiftItemId(delivery);
        const idx = groups.findIndex((g) => g.giftItems.includes(itemId));
        const covered = (pool: GiftDeliveryForAdmin[]) =>
          pool.every((d) => siblingTargets.includes(d));
        // 구 주문의 대표 카드가 다른 브랜드 디자인이면 건드리지 않는다
        // (브랜드가 섞인 주문에서 B 배송 건을 고쳤다고 A 카드였던 대표가 B 카드로 바뀌면 안 됨)
        const brandTemplateIds = new Set(
          ((brands ?? []).find((b) => b._id === getBrandId(delivery))?.thanksPrint?.templates ?? [])
            .map((t) => t._id)
            .filter(Boolean),
        );
        const ownsRepresentative =
          !representativeTemplateId || brandTemplateIds.has(representativeTemplateId);
        if (groups.length === 0) {
          if (ownsRepresentative && covered(siblings)) {
            await updateGiftOrderThanksPrint(orderId, value);
          }
        } else if (idx >= 0) {
          const inGroup = new Set(groups[idx]?.giftItems ?? []);
          if (covered(siblings.filter((d) => inGroup.has(getGiftItemId(d))))) {
            await updateGiftOrderThanksPrint(orderId, value, idx);
          }
        }
      } catch (err) {
        console.error(err);
        orderSyncFailed = true;
      }
    }

    setIsSaving(false);
    setIsEditing(false);
    onSaved?.(delivery._id, value);

    const notes: string[] = [];
    if (failed.length) notes.push(`같은 주문의 다른 배송 ${failed.length}건은 저장에 실패했습니다.`);
    if (orderSyncFailed) notes.push('주문 원본에는 반영되지 않았습니다(배송 건에는 저장됨).');
    if (isOrdered) notes.push('이미 발주된 건입니다. 업체에 변경 내용을 알려주세요.');
    if (notes.length) alert(notes.join('\n'));
  };

  const cancelEdit = () => {
    if (isNewCard) {
      onClose();
      return;
    }
    setIsEditing(false);
  };

  const title = isEditing ? (isNewCard ? '감사카드 추가' : '감사카드 수정') : '감사카드 인쇄';
  const previewTemplate = isEditing ? formTemplate : viewTemplate;
  // 미리보기 문구도 저장될 값 기준(스티커형은 이름만)
  const previewMessage = isEditing
    ? hasMessageArea
      ? form.message
      : ''
    : tp?.message ?? '';
  const viewHasMessageArea = isThanksPrintBoxSet(viewTemplate?.messageBox);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold">{title}</h3>
            <p className="truncate text-xs text-gray-500">
              {delivery.recipient?.name ?? '수령인 미상'} · {getBrandItemName(delivery)} ·{' '}
              {statusLabel[delivery.deliveryStatus]?.text ?? delivery.deliveryStatus}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="닫기"
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            {/* 이미지 미리보기 (인쇄 대상) — 수정 중에는 입력값이 바로 반영된다 */}
            {previewTemplate ? (
              <div id="thanks-card-print" className="w-full shrink-0 md:w-1/2">
                <GiftThanksPrintPreview
                  template={previewTemplate}
                  message={previewMessage}
                  sender={isEditing ? composedSender : tp?.senderName ?? ''}
                />
              </div>
            ) : (
              <div className="w-full rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-500 md:w-1/2">
                {isEditing && !candidates.length
                  ? '이 상품에 쓸 수 있는 감사카드 디자인이 없습니다.'
                  : '감사카드 템플릿을 찾을 수 없습니다.'}
              </div>
            )}

            {isEditing ? (
              <div className="w-full space-y-3 md:w-1/2">
                {isOrdered && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    이미 발주된 건 · 저장 후 업체에 변경 내용 전달 필요
                  </p>
                )}

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">
                    카드 디자인
                    {getItemInfo(delivery)?.thanksPrintTemplates?.length ? (
                      <span className="ml-1 font-normal text-gray-400">이 상품 허용 디자인만</span>
                    ) : null}
                  </label>
                  <select
                    value={form.templateId}
                    onChange={(e) => setForm((p) => ({ ...p, templateId: e.target.value }))}
                    aria-label="카드 디자인"
                    className={inputClassName}
                  >
                    {!form.templateId && <option value="">선택</option>}
                    {candidates.map((t, i) => (
                      <option key={t._id} value={t._id}>
                        {t.name || `디자인 ${i + 1}`}
                        {t.enabled === false ? ' (사용중지)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {!hasMessageArea ? (
                  formTemplate && (
                    <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                      문구 인쇄된 디자인 · 보내는 분만 인쇄
                    </p>
                  )
                ) : presets.length > 0 ? (
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-600">
                      감사글 <span className="font-normal text-gray-400">지정 문구 중 선택</span>
                    </label>
                    <div className="space-y-1.5">
                      {presets.map((p) => {
                        const active =
                          normalizeThanksPreset(p) === normalizeThanksPreset(form.message);
                        return (
                          <button
                            key={p}
                            type="button"
                            onClick={() => {
                              setTouched((t) => ({ ...t, message: true }));
                              setForm((prev) => ({ ...prev, message: p }));
                            }}
                            className={`block w-full rounded-md border px-3 py-2 text-left text-sm ${
                              active
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {oneLine(p)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-600">감사글</label>
                      <span className="text-xs text-gray-400">
                        {thanksMessageCharCount(form.message)}/{THANKS_MESSAGE_MAX_CHARS}자 ·
                        최대 {THANKS_MESSAGE_MAX_LINES}줄
                      </span>
                    </div>
                    {/* 스토어 기본 문구 — 상주 관계·고인 성별로 "저희 ○○ 상중" 치환 */}
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {THANKS_MESSAGE_PRESETS.map((preset, idx) => {
                        const filled = clampThanksMessage(
                          fillThanksMessage(preset, mourner?.relationType, mourner?.deceasedSex),
                        );
                        const active = filled === form.message;
                        return (
                          <button
                            key={preset}
                            type="button"
                            title={filled}
                            onClick={() => {
                              setTouched((t) => ({ ...t, message: true }));
                              setForm((prev) => ({ ...prev, message: filled }));
                            }}
                            className={`rounded-full border px-2.5 py-1 text-xs ${
                              active
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            기본 문구 {idx + 1}
                          </button>
                        );
                      })}
                    </div>
                    <textarea
                      value={form.message}
                      onChange={(e) => {
                        setTouched((t) => ({ ...t, message: true }));
                        setForm((p) => ({ ...p, message: clampThanksMessage(e.target.value) }));
                      }}
                      rows={4}
                      placeholder="인쇄될 감사글"
                      className={`${inputClassName} resize-none`}
                    />
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">
                    보내는 분
                    {nameLimit !== undefined && (
                      <span className="ml-1 font-normal text-gray-400">
                        이름 {nameLimit}자 이내
                      </span>
                    )}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.senderName}
                      maxLength={nameLimit}
                      onChange={(e) => {
                        setTouched((t) => ({ ...t, sender: true }));
                        setForm((p) => ({ ...p, senderName: e.target.value }));
                      }}
                      placeholder={THANKS_FALLBACK_NAME}
                      aria-label="보내는 분 이름"
                      className={`${inputClassName} ${nameTooLong ? 'border-red-400' : ''}`}
                    />
                    <select
                      value={form.suffix}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, suffix: e.target.value as ThanksSenderSuffix }))
                      }
                      className="w-24 shrink-0 rounded-md border border-gray-300 px-2 py-2 text-sm"
                    >
                      {THANKS_SENDER_SUFFIXES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    인쇄 문구: {composedSender || '-'}
                  </p>
                </div>

                {siblings.length > 0 && (
                  <div className="rounded-lg border bg-gray-50 p-3">
                    <p className="text-xs font-semibold text-gray-600">
                      같은 주문의 다른 배송 {siblings.length}건 · 체크한 건에 함께 적용
                    </p>
                    <ul className="mt-2 max-h-32 space-y-1 overflow-y-auto">
                      {siblings.map((d) => {
                        const allows = siblingAllows.get(d._id);
                        return (
                          <li key={d._id}>
                            <label
                              className={`flex items-center gap-2 text-xs ${
                                allows ? 'cursor-pointer text-gray-700' : 'text-gray-400'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={!!allows && siblingIds.has(d._id)}
                                disabled={!allows}
                                onChange={() => toggleSibling(d._id)}
                              />
                              <span className="truncate">
                                {d.recipient?.name ?? '수령인 미상'} · {getBrandItemName(d)}
                              </span>
                              <span className="shrink-0 text-gray-400">
                                {statusLabel[d.deliveryStatus]?.text ?? d.deliveryStatus}
                                {!allows
                                  ? ' · 이 디자인 불가 상품'
                                  : tp && !isSameCard(d.thanksPrint, tp)
                                  ? ' · 문구 다름'
                                  : ''}
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {siblingsFailed && (
                  <p className="text-xs text-gray-400">
                    같은 주문의 다른 배송 조회 실패 · 이 건만 저장됩니다
                  </p>
                )}
              </div>
            ) : (
              /* 복사 패널 — PSD 등에 붙여넣어 인쇄할 수 있도록 텍스트 제공 */
              <div className="w-full space-y-3 md:w-1/2">
                <p className="text-xs text-gray-400">
                  PSD 등에 붙여넣어 인쇄할 수 있도록 복사하세요.
                </p>
                {viewTemplate && !viewHasMessageArea ? (
                  <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    문구 인쇄된 디자인 · 보내는 분만 인쇄
                  </p>
                ) : (
                  <CopyField label="감사글" value={tp?.message ?? ''} multiline />
                )}
                <CopyField label="보내는 분 (배상)" value={tp?.senderName ?? ''} />
              </div>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="flex items-center gap-2 border-t px-4 py-3">
            <button
              onClick={cancelEdit}
              disabled={isSaving}
              className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={!!invalidReason || isSaving}
              title={invalidReason}
              className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving
                ? '저장중...'
                : invalidReason ||
                  (siblingTargets.length ? `${siblingTargets.length + 1}건 저장` : '저장')}
            </button>
          </div>
        ) : (
          <div className="flex gap-2 border-t px-4 py-3">
            <button
              onClick={onClose}
              className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
            >
              닫기
            </button>
            {editable && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex-1 rounded-lg border border-indigo-300 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
              >
                수정
              </button>
            )}
            <button
              onClick={() => window.print()}
              disabled={!viewTemplate}
              className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              인쇄하기
            </button>
          </div>
        )}
      </div>

      {/* 인쇄 시 감사카드만 출력 */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #thanks-card-print, #thanks-card-print * { visibility: visible !important; }
          #thanks-card-print {
            position: fixed !important;
            top: 0; left: 0; right: 0;
            margin: 0 auto !important;
            width: 100% !important;
            max-width: 600px !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @page { margin: 12mm; }
        }
      `}</style>
    </div>
  );
};

// 감사카드 미리보기 + 인쇄 (+ admin: 문구 수정 / 카드 없는 건에 추가)
export const GiftBrandThanksCardModal: FC<Props> = function GiftBrandThanksCardModal({
  delivery,
  ...rest
}) {
  if (!delivery) return null;
  // 카드가 없는 건은 수정 권한이 있을 때만 열린다(작성 화면)
  if (!delivery.thanksPrint && !rest.editable) return null;
  // 배송 건이 바뀌면 폼·편집 상태를 새로 시작
  return <ThanksCardModalInner key={delivery._id} delivery={delivery} {...rest} />;
};
